import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { devLog, devWarn } from '@/utils/core/devLog';

/**
 * Represents a supported language in the application
 */
export interface Language {
  id: number;
  language_code: string;
  language_name: string;
  flag_image_url: string | null;
}

/**
 * Result of language detection with support status and user messaging
 */
export interface DetectionResult {
  detectedLanguage: string;
  isSupported: boolean;
  detectedLanguageName: string;
  fallbackMessage: string | null;
  browserLanguage: string;
  performanceMetrics?: PerformanceMetrics;
}

/**
 * Performance metrics for language detection
 */
export interface PerformanceMetrics {
  detectionTime: number;
  timestamp: number;
}

/**
 * Configuration options for language detection behavior
 */
export interface LanguageDetectionConfig {
  /**
   * Custom navigator object for testing
   */
  navigatorOverride?: Pick<Navigator, "language" | "languages">;
  /**
   * Default fallback language code
   * @default 'en'
   */
  defaultLanguage?: string;
  /**
   * Enable debug logging
   * @default process.env['NODE_ENV'] === 'development'
   */
  debug?: boolean;
  /**
   * Enable performance monitoring
   * @default false
   */
  enablePerformanceMonitoring?: boolean;
  /**
   * Custom fallback message template
   * @param detectedLang - The detected language name
   * @param selectedLang - The selected language name
   * @returns Custom fallback message
   */
  customFallbackMessage?: (
    detectedLang: string,
    selectedLang: string
  ) => string;
  /**
   * Disable input validation warnings
   * @default false
   */
  disableValidation?: boolean;
}

/**
 * Comprehensive mapping of browser language codes to standardized codes.
 * Supports regional variants and automatically normalizes to base codes.
 */
const LANGUAGE_MAPPING: Record<string, string> = {
  // English variants
  en: "en",
  "en-US": "en",
  "en-GB": "en",
  "en-CA": "en",
  "en-AU": "en",
  "en-NZ": "en",
  "en-IE": "en",
  "en-ZA": "en",
  // German variants
  de: "de",
  "de-DE": "de",
  "de-AT": "de",
  "de-CH": "de",
  "de-LU": "de",
  "de-LI": "de",
  // French variants
  fr: "fr",
  "fr-FR": "fr",
  "fr-CA": "fr",
  "fr-BE": "fr",
  "fr-CH": "fr",
  "fr-LU": "fr",
  // Dutch variants
  nl: "nl",
  "nl-NL": "nl",
  "nl-BE": "nl",
  // Spanish variants
  es: "es",
  "es-ES": "es",
  "es-MX": "es",
  "es-AR": "es",
  "es-CO": "es",
  "es-CL": "es",
  "es-PE": "es",
  "es-VE": "es",
  // Portuguese variants
  pt: "pt",
  "pt-BR": "pt",
  "pt-PT": "pt",
  // Italian variants
  it: "it",
  "it-IT": "it",
  "it-CH": "it",
  // Russian variants
  ru: "ru",
  "ru-RU": "ru",
  "ru-BY": "ru",
  "ru-KZ": "ru",
  // Chinese variants
  zh: "zh",
  "zh-CN": "zh",
  "zh-TW": "zh",
  "zh-HK": "zh",
  // Japanese
  ja: "ja",
  "ja-JP": "ja",
  // Korean
  ko: "ko",
  "ko-KR": "ko",
  // Arabic variants
  ar: "ar",
  "ar-SA": "ar",
  "ar-AE": "ar",
  "ar-EG": "ar",
  // Hindi
  hi: "hi",
  "hi-IN": "hi",
  // Turkish
  tr: "tr",
  "tr-TR": "tr",
  // Polish
  pl: "pl",
  "pl-PL": "pl",
  // Swedish
  sv: "sv",
  "sv-SE": "sv",
  // Danish
  da: "da",
  "da-DK": "da",
  // Norwegian
  no: "no",
  nb: "no",
  nn: "no",
  "no-NO": "no",
  // Finnish
  fi: "fi",
  "fi-FI": "fi",
} as const;

/**
 * Fallback language names for environments without Intl.DisplayNames support
 */
const FALLBACK_LANGUAGE_NAMES: Record<string, string> = {
  en: "English",
  de: "German",
  fr: "French",
  nl: "Dutch",
  es: "Spanish",
  pt: "Portuguese",
  it: "Italian",
  ru: "Russian",
  zh: "Chinese",
  ja: "Japanese",
  ko: "Korean",
  ar: "Arabic",
  hi: "Hindi",
  tr: "Turkish",
  pl: "Polish",
  sv: "Swedish",
  da: "Danish",
  no: "Norwegian",
  fi: "Finnish",
} as const;

// Environment detection
const isBrowser = typeof window !== "undefined";
const isDevelopment = process.env['NODE_ENV'] === "development";

// Cache management
const MAX_CACHE_SIZE = 100;
const mappingCache = new Map<string, string>();
const cacheStats = {
  hits: 0,
  misses: 0,
  lastCleared: Date.now(),
};

/**
 * Safe performance.now() access with fallback
 */
const getPerformanceNow = (): number => {
  if (isBrowser && typeof performance !== "undefined" && performance.now) {
    return performance.now();
  }
  return Date.now();
};

/**
 * Validates the supported languages array for common issues
 */
const validateSupportedLanguages = (
  languages: Language[],
  disableValidation: boolean = false
): void => {
  if (disableValidation) return;

  if (languages.length === 0) {
    devWarn("⚠️ useLanguageDetection: supportedLanguages array is empty.");
    return;
  }

  const languageCodes = languages.map((l) => l.language_code);
  const uniqueCodes = new Set(languageCodes);

  if (uniqueCodes.size !== languageCodes.length) {
    const duplicates = languageCodes.filter(
      (code, index) => languageCodes.indexOf(code) !== index
    );
    const uniqueDuplicates = Array.from(new Set(duplicates));
    devWarn(
      `⚠️ useLanguageDetection: duplicate language codes: ${uniqueDuplicates.join(
        ", "
      )}`
    );
  }

  const invalidLanguages = languages.filter(
    (lang) => !lang.language_code?.trim()
  );

  if (invalidLanguages.length > 0) {
    devWarn(
      `⚠️ useLanguageDetection: ${invalidLanguages.length} language(s) have invalid codes`
    );
  }
};

/**
 * Safely retrieves the display name for a language code
 */
const getLanguageDisplayName = (
  code: string,
  supportedLanguages: Language[]
): string => {
  try {
    // Priority 1: Use language_name from supported languages
    const supportedLang = supportedLanguages.find(
      (lang) => lang.language_code === code
    );
    if (supportedLang?.language_name) {
      return supportedLang.language_name;
    }

    // Priority 2: Use Intl.DisplayNames API
    if (typeof Intl !== "undefined" && Intl.DisplayNames) {
      const displayNames = new Intl.DisplayNames(["en"], { type: "language" });
      const displayName = displayNames.of(code);
      if (displayName && displayName !== code) {
        return displayName;
      }
    }
  } catch (error) {
    if (isDevelopment) {
      devWarn(`Intl.DisplayNames failed for "${code}":`, error);
    }
  }

  // Priority 3: Use predefined fallback names
  if (FALLBACK_LANGUAGE_NAMES[code]) {
    return FALLBACK_LANGUAGE_NAMES[code];
  }

  // Priority 4: Return the code itself
  return code.toUpperCase();
};

/**
 * Clears the language mapping cache manually
 */
export const clearLanguageMappingCache = (): void => {
  const size = mappingCache.size;
  mappingCache.clear();
  cacheStats.lastCleared = Date.now();

  if (isDevelopment) {
    devLog(`🧹 Language mapping cache cleared (${size} entries)`);
  }
};

/**
 * Gets current cache statistics for monitoring and debugging
 */
export const getLanguageMappingCacheStats = (): {
  size: number;
  hits: number;
  misses: number;
  hitRate: string;
  lastCleared: string;
} | null => {
  if (!isBrowser) return null;

  const total = cacheStats.hits + cacheStats.misses;
  const hitRate =
    total > 0 ? ((cacheStats.hits / total) * 100).toFixed(1) : "0";

  return {
    size: mappingCache.size,
    hits: cacheStats.hits,
    misses: cacheStats.misses,
    hitRate,
    lastCleared: new Date(cacheStats.lastCleared).toISOString(),
  };
};

/**
 * Maps a browser language code to a standardized application language code
 */
const mapBrowserLanguage = (
  browserLang: string,
  defaultLang: string = "en"
): string => {
  const cacheKey = `${browserLang}-${defaultLang}`;
  if (mappingCache.has(cacheKey)) {
    cacheStats.hits++;
    return mappingCache.get(cacheKey)!;
  }

  cacheStats.misses++;

  const normalized = browserLang.toLowerCase().trim();
  let result: string;

  if (LANGUAGE_MAPPING[normalized]) {
    result = LANGUAGE_MAPPING[normalized];
  } else {
    const baseLang = normalized.split("-")[0]!;
    result = LANGUAGE_MAPPING[baseLang] || defaultLang;
  }

  // Cache management
  if (mappingCache.size >= MAX_CACHE_SIZE) {
    const firstKey = mappingCache.keys().next().value;
    if (firstKey) mappingCache.delete(firstKey);
  }

  mappingCache.set(cacheKey, result);
  return result;
};

/**
 * Creates a stable hash of the supported languages array for dependency tracking
 */
const getLanguagesHash = (languages: Language[]): string => {
  return languages
    .map((lang) => `${lang.language_code}:${lang.language_name}`)
    .sort()
    .join("|");
};

/**
 * Performance measurement hook
 */
const usePerformanceMeasurement = (enable: boolean) => {
  const startTimeRef = useRef(0);

  const start = useCallback(() => {
    if (enable) startTimeRef.current = getPerformanceNow();
  }, [enable]);

  const end = useCallback(() => {
    if (!enable) return 0;
    return getPerformanceNow() - startTimeRef.current;
  }, [enable]);

  return { start, end };
};

/**
 * Main language detection hook with database validation
 */
export const useLanguageDetection = (
  supportedLanguages: Language[],
  config: LanguageDetectionConfig = {}
): DetectionResult => {
  const {
    navigatorOverride,
    defaultLanguage = "en",
    debug = isDevelopment,
    enablePerformanceMonitoring = false,
    customFallbackMessage,
    disableValidation = false,
  } = config;

  // Cache management effect
  useEffect(() => {
    if (isBrowser && isDevelopment) {
      const CACHE_CLEAR_INTERVAL = 5 * 60 * 1000; // 5 minutes

      const interval = setInterval(() => {
        const size = mappingCache.size;
        mappingCache.clear();
        cacheStats.lastCleared = Date.now();

        if (debug) {
          devLog(`🧹 Auto-cleared cache (${size} entries)`);
        }
      }, CACHE_CLEAR_INTERVAL);

      return () => clearInterval(interval);
    }
    return undefined;
  }, [debug]);

  // Performance measurement
  const { start: startPerformance, end: endPerformance } =
    usePerformanceMeasurement(enablePerformanceMonitoring);

  // Navigator instance
  const nav = useMemo(
    () => navigatorOverride || (isBrowser ? navigator : null),
    [navigatorOverride]
  );

  // Input validation
  const validationPerformed = useRef(false);
  useEffect(() => {
    if (!validationPerformed.current && supportedLanguages.length > 0) {
      validateSupportedLanguages(supportedLanguages, disableValidation);
      validationPerformed.current = true;
    }
  }, [supportedLanguages, disableValidation]);

  // Language hash for dependency tracking
  const languagesHash = useMemo(
    () => getLanguagesHash(supportedLanguages),
    [supportedLanguages]
  );

  // Memoized language detection logic
  const detectLanguage = useCallback((): DetectionResult => {
    startPerformance();

    // Handle SSR or missing navigator
    if (!nav) {
      const detectionTime = endPerformance();
      const isDefaultSupported = supportedLanguages.some(
        (lang) => lang.language_code === defaultLanguage
      );
      const finalLanguageName = getLanguageDisplayName(
        defaultLanguage,
        supportedLanguages
      );

      return {
        detectedLanguage: defaultLanguage,
        isSupported: isDefaultSupported,
        detectedLanguageName: finalLanguageName,
        fallbackMessage: null,
        browserLanguage: "unknown",
        ...(enablePerformanceMonitoring && {
          performanceMetrics: { detectionTime, timestamp: Date.now() },
        }),
      };
    }

    // Get browser languages
    const browserLanguages =
      nav.languages || (nav.language ? [nav.language] : [defaultLanguage]);
    let selectedLanguage: string | null = null;
    let browserLanguage = browserLanguages[0] || defaultLanguage;

    // Try to find supported language from browser preferences
    for (const browserLang of browserLanguages) {
      const mappedLang = mapBrowserLanguage(browserLang, defaultLanguage);

      // Check if mapped language exists in database (supportedLanguages)
      const isInDatabase = supportedLanguages.some(
        (lang) => lang.language_code === mappedLang
      );

      if (isInDatabase) {
        selectedLanguage = mappedLang;
        browserLanguage = browserLang;
        break;
      }
    }

    // If no supported language found, default to English or first available
    if (selectedLanguage === null) {
      const englishInDb = supportedLanguages.some(
        (lang) => lang.language_code === "en"
      );
      if (englishInDb) {
        selectedLanguage = "en";
      } else if (supportedLanguages.length > 0) {
        selectedLanguage = supportedLanguages[0]!.language_code;
      } else {
        selectedLanguage = defaultLanguage;
      }
    }

    const finalLanguage = selectedLanguage;
    const baseBrowserLang = browserLanguage.split("-")[0]!.toLowerCase();

    // Check if browser language is supported
    const browserLangSupported = supportedLanguages.some(
      (lang) => lang.language_code === baseBrowserLang
    );

    const isSupported =
      browserLangSupported && finalLanguage === baseBrowserLang;

    // Get display names
    const finalLanguageName = getLanguageDisplayName(
      finalLanguage,
      supportedLanguages
    );
    const browserLangName = getLanguageDisplayName(
      baseBrowserLang,
      supportedLanguages
    );

    // Generate fallback message
    let fallbackMessage: string | null = null;
    if (!browserLangSupported && baseBrowserLang !== finalLanguage) {
      fallbackMessage = customFallbackMessage
        ? customFallbackMessage(browserLangName, finalLanguageName)
        : `Your system language (${browserLangName}) isn't supported yet. The site is displayed in ${finalLanguageName}.`;
    }

    const detectionTime = endPerformance();

    const result: DetectionResult = {
      detectedLanguage: finalLanguage,
      isSupported,
      detectedLanguageName: finalLanguageName,
      fallbackMessage,
      browserLanguage,
      ...(enablePerformanceMonitoring && {
        performanceMetrics: { detectionTime, timestamp: Date.now() },
      }),
    };

    // Debug logging
    if (debug) {
      devLog("🌍 Language Detection:", {
        browserLanguages: [...browserLanguages],
        selectedLanguage: finalLanguage,
        isSupported,
        fallbackMessage,
        supportedCodes: supportedLanguages.map((lang) => lang.language_code),
        detectionTime: enablePerformanceMonitoring ? detectionTime : undefined,
      });
    }

    return result;
  }, [
    nav,
    defaultLanguage,
    supportedLanguages,
    debug,
    enablePerformanceMonitoring,
    customFallbackMessage,
    startPerformance,
    endPerformance,
  ]);

  // State management
  const [detectionResult, setDetectionResult] = useState<DetectionResult>(() =>
    supportedLanguages.length > 0
      ? detectLanguage()
      : {
          detectedLanguage: defaultLanguage,
          isSupported: false,
          detectedLanguageName: getLanguageDisplayName(
            defaultLanguage,
            supportedLanguages
          ),
          fallbackMessage: "No supported languages provided",
          browserLanguage: "unknown",
        }
  );

  // Re-run detection when dependencies change
  useEffect(() => {
    if (supportedLanguages.length > 0) {
      setDetectionResult(detectLanguage());
    }
  }, [supportedLanguages, detectLanguage, languagesHash]);

  return detectionResult;
};

/**
 * Simplified hook for basic language detection
 */
export const useSimpleLanguageDetection = (
  supportedLanguages: Language[]
): string => {
  const { detectedLanguage } = useLanguageDetection(supportedLanguages);
  return detectedLanguage;
};

export default useLanguageDetection;
